import { Component, computed, input, model, output } from '@angular/core';
import { type CourtScheduleItem, DAY_OF_WEEK_OPTIONS } from './court-schedule-list.model';

const DAY_OF_WEEK_ORDER: Record<string, number> = Object.fromEntries(
  DAY_OF_WEEK_OPTIONS.map((day, index) => [day.value, index]),
);

@Component({
  selector: 'lib-court-schedule-list',
  standalone: true,
  styleUrls: [
    '../../tokens/colors.css',
    './court-schedule-list.component.css',
  ],
  template: `
    @if (title()) {
      <h3 class="msa-csl__title">{{ title() }}</h3>
    }
    <div class="msa-csl__tabs">
      @for (day of days(); track day.value) {
        <button
          type="button"
          class="msa-csl__tab"
          [class.msa-csl__tab--active]="activeDay() === day.value"
          (click)="activeDay.set(day.value)"
        >
          {{ day.label }}
          @if (countByDay(day.value); as count) {
            <span class="msa-csl__tab-count">{{ count }}</span>
          }
        </button>
      }
    </div>
    @if (visibleSchedules().length > 0) {
      <div class="msa-csl__list">
        @for (item of visibleSchedules(); track item.documentId ?? item.id ?? $index) {
          <div class="msa-csl__row">
            <div class="msa-csl__info">
              <span class="msa-csl__time">{{ formatTime(item.startTime) }} — {{ formatTime(item.endTime) }}</span>
              <span class="msa-csl__price">{{ formatPrice(item.priceCents) }}</span>
            </div>
            @if (showEditAction() || showDeleteAction()) {
              <div class="msa-csl__actions">
                @if (showEditAction()) {
                  <button
                    type="button"
                    class="msa-csl__action msa-csl__action--edit"
                    (click)="editItem.emit(item)"
                  >
                    Editar
                  </button>
                }
                @if (showDeleteAction()) {
                  <button
                    type="button"
                    class="msa-csl__action msa-csl__action--delete"
                    (click)="deleteItem.emit(item)"
                  >
                    Eliminar
                  </button>
                }
              </div>
            }
          </div>
        }
      </div>
    } @else {
      <p class="msa-csl__empty">{{ emptyMessage() }}</p>
    }
  `,
})
export class CourtScheduleListComponent {
  readonly title = input<string>();
  readonly courtSchedules = input.required<CourtScheduleItem[]>();
  readonly days = input<readonly { value: string; label: string }[]>(DAY_OF_WEEK_OPTIONS);
  readonly showDeleteAction = input<boolean>(true);
  readonly showEditAction = input<boolean>(false);
  readonly emptyMessage = input<string>('Sin horarios configurados.');

  readonly deleteItem = output<CourtScheduleItem>();
  readonly editItem = output<CourtScheduleItem>();

  /** Two-way bindable — bind to the same parent property as a sibling court-schedule-form's `dayOfWeek` to keep both in sync. */
  readonly activeDay = model<string>(DAY_OF_WEEK_OPTIONS[0]?.value ?? 'lunes');

  readonly sortedSchedules = computed(() => {
    return [...this.courtSchedules()].sort((a, b) => {
      const dayDiff = (DAY_OF_WEEK_ORDER[a.dayOfWeek] ?? 99) - (DAY_OF_WEEK_ORDER[b.dayOfWeek] ?? 99);
      if (dayDiff !== 0) return dayDiff;
      return a.startTime.localeCompare(b.startTime);
    });
  });

  readonly visibleSchedules = computed(() =>
    this.sortedSchedules().filter((item) => item.dayOfWeek === this.activeDay()),
  );

  countByDay(dayOfWeek: string): number {
    return this.courtSchedules().filter((item) => item.dayOfWeek === dayOfWeek).length;
  }

  formatTime(time: string): string {
    return time.slice(0, 5);
  }

  formatPrice(priceCents: number): string {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      maximumFractionDigits: 0,
    }).format(priceCents);
  }
}
